import React, {
  ReactNode,
  useMemo,
  useState,
  useCallback,
  useRef,
  useContext,
} from 'react';
import { Avatar, Upload, Modal, Button, message, Spin } from 'antd';
import min from 'lodash/min';
import get from 'lodash/get';
import findLastIndex from 'lodash/findLastIndex';
import IconEditPenCircle from '../Icons/IconEditPenCircle';
import IconCloseOutlined from '../Icons/IconCloseOutlined';
import IconCamera from '../Icons/IconCamera';
import IconUploadBulk from '../Icons/IconUploadBulk';
import IconPlusOutlined from '../Icons/IconPlusOutlined';
import { AvatarSizeType } from '../../types';
import { randomHex } from '../../utils/helper';
import { RcFile, UploadChangeParam } from 'antd/lib/upload';
import { UploadFile } from 'antd/lib/upload/interface';
import Text from '../Text';
import Webcam from 'react-webcam';
import { ConfigContext } from '../../context/Config';
import './ProfileAvatar.less';

export type ProfileAvatarType = {
  src?: string;
  name?: ReactNode;
  alt?: string;
  size?: AvatarSizeType;
  description?: ReactNode;
  fileList?: UploadFile<any>[];
  onRemoveFile?: (path: string) => Promise<void>;
  onUploadChange?: (info: UploadChangeParam<UploadFile<any>>) => void;
  onWebCamUploadChange?: (imageSrc: string) => Promise<void>;
  uploadAction?:
    | string
    | ((file: RcFile) => string)
    | ((file: RcFile) => PromiseLike<string>);
  uploadIcon?: ReactNode;
};
const sizeMapping: { [key in AvatarSizeType]: number } = {
  xxs: 16,
  xs: 24,
  x: 32,
  sm: 40,
  md: 44,
  lg: 48,
  lm: 52,
  l: 64,
  xl: 74,
  xxl: 94,
  xxxl: 116,
  xxxxl: 200,
  ssm: 80,
};
const nameFirstLetters = (alt = '') => {
  const arr = alt
    .split(' ')
    .map((s) => s.trim())
    .filter(Boolean);

  while (arr.length > 2) {
    const minVal = min(arr.map((s) => s.length));
    arr.splice(
      findLastIndex(arr, (s) => s.length === minVal),
      1,
    );
  }

  return arr.map((s) => s.charAt(0).toLocaleUpperCase()).join('');
};

const ProfileAvatar: React.FC<ProfileAvatarType> = ({
  size = 'md',
  children,
  description,
  uploadAction,
  fileList,
  onUploadChange = () => {},
  onWebCamUploadChange = async () => {},
  onRemoveFile = async () => {},
  uploadIcon,
  ...props
}) => {
  const { translate } = useContext(ConfigContext);
  const { src, alt, name } = props;
  const classNames = ['t-avatar'];
  const randomColorHex = useMemo(randomHex, []);

  const [actionType, setActionType] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [permission, setPermission] = useState(false);
  const [uploadedFile, setUploadedFile] = useState();
  const [captureLoading, setCaptureLoading] = useState(false);

  const handleModalClose = () => {
    setIsModalVisible(false);
    setActionType(null);
    setUploadedFile(null);
  };

  // Capture webcam image
  const webcamRef = useRef(null);
  const capture = useCallback(() => {
    const imageSrc = webcamRef.current && webcamRef.current.getScreenshot();
    setCaptureLoading(true);
    onWebCamUploadChange(imageSrc).then(() => {
      setCaptureLoading(false);
    });
  }, [webcamRef]);

  const handleAccess = useCallback(
    (type) => {
      setActionType(type);
      if (type === 'upload') {
        return;
      }
      navigator.mediaDevices
        .getUserMedia({ video: true })
        .then((stream) => {
          setPermission(get(stream, 'active', 'false'));
        })
        .catch(() => {
          message.error('Please grant access to your media');
        });
    },
    [fileList, permission, uploadedFile],
  );

  function renderActionButtons() {
    return (
      <div className="t-actions_btn">
        <Button
          type="primary"
          onClick={() => handleAccess('upload')}
          icon={
            <span>
              <IconPlusOutlined fill="currentcolor" size="sm" />
            </span>
          }
        >
          {translate('Upload a photo')}
        </Button>
        <Button
          type="default"
          onClick={() => handleAccess('camera')}
          icon={
            <span className="t-camera_icon">
              <IconCamera fill="currentcolor" size="md" />
            </span>
          }
        >
          {translate('Take a photo')}
        </Button>
      </div>
    );
  }

  // TODO: implement backend validation
  const handleBeforeUpload = (file, fileList) => {
    const isJpgOrPng =
      file.type === 'image/jpeg' ||
      file.type === 'image/png' ||
      file.type === 'image/jpg';

    if (!isJpgOrPng) {
      message.error(translate('You can only upload JPG/PNG file!'));
    }

    if (fileList) {
      setUploadedFile(fileList);
    }
    return isJpgOrPng;
  };

  const handleRemoveFile = (file) => {
    onRemoveFile(file.src || file.response.path);
    setUploadedFile(null);
  };

  const renderFileUpload = () => {
    return (
      <Upload
        fileList={fileList}
        showUploadList={true}
        multiple={false}
        onChange={onUploadChange}
        onRemove={handleRemoveFile}
        action={uploadAction}
        beforeUpload={handleBeforeUpload}
        listType="picture"
        accept="image/jpeg, image/png, image/jpg"
      >
        {!uploadedFile && (
          <Button
            className="t-attachment_btn"
            icon={
              <span>
                <IconUploadBulk fill="currentcolor" size="sm" />
              </span>
            }
          >
            {translate('Attach a photo')}
          </Button>
        )}
      </Upload>
    );
  };

  const renderWebCam = () => {
    return !permission ? (
      <div className="t-spinner">
        <Spin size="default" spinning />
      </div>
    ) : (
      <div className="t-camera-container">
        <Webcam
          audio={false}
          ref={webcamRef}
          screenshotFormat="image/jpeg"
          width={'100%'}
        />
        <Button
          loading={captureLoading}
          disabled={!permission}
          onClick={capture}
          className="t-closeBadge"
          type="primary"
          shape="circle"
          icon={
            <span className="t-camera_icon">
              <IconCamera fill="currentcolor" size="md" />
            </span>
          }
        />
      </div>
    );
  };

  return (
    <div className={`t-profile-avatar-container`}>
      <div className={`t-avatar-wrapper`}>
        <Avatar
          className={classNames.join(' ')}
          size={sizeMapping[size]}
          {...props}
          style={{ background: !src && randomColorHex }}
        >
          {nameFirstLetters(alt) || children}
        </Avatar>
        {uploadAction && size === 'xxxxl' && (
          <div className="t-upload-wrapper">
            <IconEditPenCircle
              onClick={() => setIsModalVisible(true)}
              size="xl"
              fill="currentcolor"
            />
          </div>
        )}
      </div>
      <div className="t-avatar-info-wrapper">
        {!!name && <div>{name}</div>}
        {!!description && <div>{description}</div>}
      </div>

      <div className="t-avatar-modal-wrapper">
        <Modal
          title={
            <Text level={2} semiBold rows={1}>
              {translate('Update profile picture')}
            </Text>
          }
          visible={isModalVisible}
          onOk={handleModalClose}
          onCancel={handleModalClose}
          width={340}
          centered
          destroyOnClose
          maskClosable={false}
          okText={translate('OK')}
          cancelText={translate('Cancel')}
          closeIcon={
            <span>
              <IconCloseOutlined fill="currentcolor" size="sm" />
            </span>
          }
        >
          {!actionType && renderActionButtons()}

          {actionType === 'camera' && renderWebCam()}

          {actionType === 'upload' && renderFileUpload()}
        </Modal>
      </div>
    </div>
  );
};

export default ProfileAvatar;
